import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';
import { useAdminQueriesCounts } from '../hooks/useAdminQueriesCounts.js';

/**
 * Wraps every authenticated admin route. Two columns:
 *   - Sidebar (sticky, full-height dark gradient)
 *   - Main column = topbar + page content (<Outlet />)
 *
 * The sidebar's nav uses react-router's NavLink so the active state
 * (`.active` class auto-added when the URL matches) drives the
 * purple-tinted highlight from the design.
 *
 * @author <lukman@zshift.io>
 */
export function Layout() {
  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <Topbar />
        <Outlet />
      </main>
    </div>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────
function Sidebar() {
  const { me, logout } = useAdminAuth();
  const { confirm } = useUI();
  const navigate = useNavigate();
  // OPEN-ticket count drives a small badge on the "Support Queries"
  // entry — the admin equivalent of the user's unread support badge.
  // Polled every 30s + on tab focus; the Queries page also exposes
  // this hook's `refresh` so sending a reply (OPEN → PENDING) drops
  // the badge in real time, not on the next poll cycle.
  const { open: openQueriesCount } = useAdminQueriesCounts();

  // Role-based nav surface. Superadmins see everything; everyone else
  // (Support lead / Support agent / Read-only) gets a curated subset
  // that matches what the server actually authorises — no point
  // showing Plans, Finances, Broadcasts, Admin Team, or Audit Logs
  // to a support agent when the underlying endpoints would 403 them
  // on click. Overview + Users + Queries are the read surfaces every
  // role can use; User Map stays visible because the data is harmless
  // signup geography. Settings stays visible so any admin can change
  // their own password after first sign-in.
  const isSuperadmin = me?.role === 'SUPERADMIN';

  const onSignOut = async () => {
    const ok = await confirm({
      title: 'Sign out of the admin console?',
      message: 'You\'ll need to sign back in to manage accounts.',
      confirmLabel: 'Sign out',
      destructive: true,
    });
    if (!ok) return;
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <aside className="sidebar">
      <div className="sb-brand">
        <BrandMark />
        <div>
          <span className="sb-brand-word">NotesHQ</span>
          <span className="sb-brand-sub">Admin · v2.4</span>
        </div>
      </div>

      <div className="sb-group">
        <span className="sb-group-label">General</span>
        <SbLink to="/"        icon="overview">Overview</SbLink>
        <SbLink to="/users"   icon="users">Users</SbLink>
        <SbLink to="/queries" icon="queries" badge={openQueriesCount || null}>Support Queries</SbLink>
        {isSuperadmin && <SbLink to="/map" icon="globe">User Map</SbLink>}
      </div>

      {/* Commerce + Outreach + the admin/audit pieces of System are
          Superadmin-only. Support roles never see these groups at all —
          empty group labels would look broken, so we conditionally
          render the entire <div>. */}
      {isSuperadmin && (
        <div className="sb-group">
          <span className="sb-group-label">Commerce</span>
          <SbLink to="/plans"    icon="plans">Plans & Features</SbLink>
          <SbLink to="/finances" icon="finances">Finances</SbLink>
        </div>
      )}

      {isSuperadmin && (
        <div className="sb-group">
          <span className="sb-group-label">Outreach</span>
          <SbLink to="/broadcasts" icon="megaphone">Broadcasts</SbLink>
        </div>
      )}

      <div className="sb-group">
        <span className="sb-group-label">System</span>
        {/* Admin Team list itself is visible to every role (the
            controller's @PreAuthorize allows GET for all admins so
            support staff can see who their colleagues are) but only
            Superadmins see Audit Logs — those rows include other
            admins' actions and can leak privileged context. */}
        <SbLink to="/admins"   icon="shield">Admin Team</SbLink>
        <SbLink to="/settings" icon="settings">Settings</SbLink>
        {isSuperadmin && <SbLink to="/logs" icon="logs">Audit Logs</SbLink>}
      </div>

      <div className="sb-foot">
        <div className="sb-me" onClick={onSignOut} title="Sign out">
          <div className="sb-av">{initials(me?.name || me?.email)}</div>
          <div className="sb-me-info">
            <strong>{me?.name || me?.email || 'Admin'}</strong>
            <em>{me?.roleLabel || me?.role || ''}</em>
          </div>
          <Icon name="chevDown" size={14} className="sb-me-caret" />
        </div>
      </div>
    </aside>
  );
}

function SbLink({ to, icon, children, badge }) {
  // Use `end` for the home route so it doesn't match every nested path.
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => `sb-link ${isActive ? 'active' : ''}`}
    >
      <Icon name={icon} size={16} />
      <span>{children}</span>
      {badge != null && <span className="sb-badge">{badge}</span>}
    </NavLink>
  );
}

// ── Topbar ───────────────────────────────────────────────────────
function Topbar() {
  const location = useLocation();
  const label = labelForPath(location.pathname);
  return (
    <div className="topbar">
      <div className="crumbs">
        <b>{label}</b>
      </div>
      <div className="top-right">
        <span className="top-env"><span className="dot"></span> PRODUCTION</span>
      </div>
    </div>
  );
}

const ROUTE_LABELS = {
  '/':         'Overview',
  '/users':    'User management',
  '/queries':  'Support queries',
  '/map':      'User map',
  '/plans':    'Plans & features',
  '/finances': 'Finances',
  '/admins':   'Admin team',
  '/settings': 'Settings',
  '/logs':     'Audit logs',
};
function labelForPath(pathname) {
  return ROUTE_LABELS[pathname] || 'Admin';
}

// ── Brand mark + initials helper ─────────────────────────────────
function BrandMark() {
  return (
    <svg width="30" height="30" viewBox="0 0 100 100" aria-hidden="true">
      <rect x="14" y="12" width="58" height="58" rx="11" fill="#B7C2F3"/>
      <rect x="18" y="18" width="58" height="58" rx="11" fill="#8092E5"/>
      <rect x="22" y="24" width="58" height="58" rx="11" fill="#2D43E0"/>
      <circle cx="75" cy="25" r="13" fill="#FF6B47"/>
      <text x="75" y="29.5" textAnchor="middle" fontFamily="Geist, system-ui, sans-serif" fontSize="11" fontWeight="700" fill="white">HQ</text>
    </svg>
  );
}

function initials(name) {
  if (!name) return '—';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
